import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit, Search } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import { AccountData, ExtendedDashboardOut, OrderData } from '../../../api';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useCalendar } from '../../../calendar/CalendarContext/CalendarContext';
import EditOrderDialog from '../../../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { TableComponent } from '../../../table/TableComponent';
import { CellProps } from '../../../table/useTable';
import { usePriceFormatter } from '../../../common/usePriceFormatter';
import { DiscountDetails } from '../../../common/DiscountDetails';
import { useLanguage } from '../../../common/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../../common/getDateAndTimeFormat';
import TimeUtils from '../../../utils/TimeUtils';
import { WorkshopCircle } from '../../../common/ui/WorkshopCircle';
import { SECTION_META, SectionType, getVisibleSectionTypes } from '../sectionMeta';
import { flattenDetailedPeriods, getSectionCounts } from './flattenDetailedPeriods';
import { UnifiedRow } from './UnifiedRow';
import { useAnalyticsFilter } from './AnalyticsFilter/AnalyticsFilterContext';
import { AnalyticsFilter } from './AnalyticsFilter/AnalyticsFilter';
import { useUnifiedRowClickHandler } from './unifiedRowClickHandler';
import { mb } from '../../../common/theme/mbTokens';
import { DATE_WINDOWS, DateWindow, persistDateWindow, readStoredDateWindow } from './dateWindow';

type Props = {
  data?: ExtendedDashboardOut;
  isLoading: boolean;
  lastFetch: moment.Moment;
  onRefresh: () => void;
};

const DEFAULT_ROWS_PER_PAGE = 25;

export const TableTab = ({ data, isLoading, lastFetch, onRefresh }: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const priceFormatter = usePriceFormatter();
  const language = useLanguage();
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);

  const { editOrderDrawerState, closeEditOrderDrawer } = useCalendar();
  const { selectedLocationIds, selectedOrderPageIds, sectionTypes, isFilterActive } = useAnalyticsFilter();

  const { onRowClick, loadingId } = useUnifiedRowClickHandler();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [dateWindow, setDateWindowState] = useState<DateWindow | null>(readStoredDateWindow);
  const setDateWindow = (v: DateWindow | null) => {
    setDateWindowState(v);
    persistDateWindow(v);
  };

  const allRows = useMemo(() => flattenDetailedPeriods(data, account), [data, account]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sectionSet = new Set(sectionTypes);
    const now = moment();
    const yesterday = now.clone().subtract(1, 'day');
    const prevMonth = now.clone().subtract(1, 'month');

    const matchesWindow = (rowDateISO: string): boolean => {
      if (!dateWindow) return true;
      const d = TimeUtils.getLocalTime(rowDateISO, account.timezone);
      switch (dateWindow) {
        case 'today':
          return d.isSame(now, 'day');
        case 'yesterday':
          return d.isSame(yesterday, 'day');
        case 'thisMonth':
          return d.isSame(now, 'month') && d.isSame(now, 'year');
        case 'previousMonth':
          return d.isSame(prevMonth, 'month') && d.isSame(prevMonth, 'year');
        default:
          return true;
      }
    };

    return allRows.filter((r) => {
      if (!sectionSet.has(r.sectionType)) return false;
      if (!matchesWindow(r.date)) return false;
      if (selectedLocationIds !== null && (!r.locationId || !selectedLocationIds.includes(r.locationId))) return false;
      if (selectedOrderPageIds !== null && (!r.orderPageId || !selectedOrderPageIds.includes(r.orderPageId)))
        return false;
      if (!q) return true;
      return (
        (r.personName ?? '').toLowerCase().includes(q) ||
        (r.code ?? '').toLowerCase().includes(q) ||
        (r.orderNumber !== undefined && String(r.orderNumber).includes(q))
      );
    });
  }, [allRows, search, sectionTypes, selectedLocationIds, selectedOrderPageIds, dateWindow, account.timezone]);

  const isNarrowed = isFilterActive || dateWindow !== null || search.trim().length > 0;

  const dateRangeLabel = useMemo(() => {
    const start = moment().subtract(1, 'month').startOf('month');
    const end = moment();
    return `${start.format(shortDateFormat)} — ${end.format(shortDateFormat)}`;
  }, [shortDateFormat]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filtered.length) {
      setPage(0);
    }
  }, [filtered.length, page, rowsPerPage]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const totals = useMemo(
    () => ({
      count: filtered.length,
      income: filtered.reduce((sum, r) => sum + (r.price ?? 0), 0),
    }),
    [filtered],
  );

  const filteredSectionCounts = useMemo(() => {
    const counts: Partial<Record<SectionType, number>> = {};
    filtered.forEach((r) => {
      counts[r.sectionType] = (counts[r.sectionType] ?? 0) + 1;
    });
    return counts;
  }, [filtered]);

  const summarySections = useMemo(() => {
    const visible = new Set(getVisibleSectionTypes(account));
    return sectionTypes.filter((s) => visible.has(s));
  }, [account, sectionTypes]);

  const sectionCounts = useMemo(() => getSectionCounts(data), [data]);
  const truncatedSections = useMemo(
    () =>
      (Object.keys(sectionCounts) as SectionType[]).filter((s) => {
        const info = sectionCounts[s];
        return info && info.count > info.rowsCount;
      }),
    [sectionCounts],
  );

  const columns: CellProps<UnifiedRow>[] = useMemo(
    () => [
      {
        id: 'open',
        alignment: 'left',
        width: 40,
        noPadding: true,
        label: '',
        render: (row) => (
          <Tooltip title={t('analytics.table.open')}>
            <IconButton
              size="small"
              disabled={loadingId !== null}
              onClick={() => {
                void onRowClick(row);
              }}>
              {loadingId === row.entityId ? <CircularProgress size={16} /> : <Edit fontSize="small" />}
            </IconButton>
          </Tooltip>
        ),
      },
      {
        id: 'section',
        alignment: 'left',
        label: t('analytics.table.section'),
        render: (row) => {
          const Icon = SECTION_META[row.sectionType].icon;
          return (
            <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
              <Icon fontSize="small" color="action" />
              <Typography variant="body2">{t(`analytics.sections.${row.sectionType}.title`)}</Typography>
            </Grid>
          );
        },
      },
      {
        id: 'date',
        alignment: 'left',
        label: t('analytics.table.created_at'),
        render: (row) => {
          const local = TimeUtils.getLocalTime(row.date, account.timezone);
          const isToday = local.isSame(moment(), 'day');
          return (
            <Typography
              variant={isToday ? 'body1' : 'body2'}
              color={isToday ? 'primary.main' : undefined}
              fontWeight={isToday ? 700 : undefined}
              dir="ltr"
              sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {local.format(isToday ? timeFormat : shortDateFormat)}
            </Typography>
          );
        },
      },
      {
        id: 'workshop',
        alignment: 'left',
        label: t('analytics.table.workshop'),
        render: (row) => {
          const label = row.orderPageLabel?.[language] ?? row.orderPageLabel?.en;
          if (!label && !row.orderPageId) return <Typography variant="body2">—</Typography>;
          return (
            <Grid container gap={1} alignItems="center" flexWrap="nowrap">
              <WorkshopCircle account={account} orderPageId={row.orderPageId} language={language} />
              <Typography variant="body2">{label ?? '—'}</Typography>
            </Grid>
          );
        },
      },
      {
        id: 'person',
        alignment: 'left',
        label: t('analytics.table.person'),
        render: (row) => <Typography variant="body2">{row.personName ?? '—'}</Typography>,
      },
      {
        id: 'ref',
        alignment: 'left',
        label: t('analytics.table.ref'),
        render: (row) => (
          <Typography variant="body2" dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.orderNumber !== undefined ? `#${row.orderNumber}` : row.code ?? '—'}
          </Typography>
        ),
      },
      {
        id: 'persons',
        alignment: 'left',
        label: t('analytics.table.persons'),
        render: (row) => (
          <Typography variant="body2" dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.persons ?? '—'}
          </Typography>
        ),
      },
      {
        id: 'price',
        alignment: 'left',
        label: t('analytics.table.price'),
        render: (row) => (
          <Grid container flexDirection="column" gap={0.25}>
            {priceFormatter(row.price, false, 'body2')}
            {row.discount && (
              <DiscountDetails discount={row.discount} initialPrice={row.price} variant="caption" showLabel={false} />
            )}
          </Grid>
        ),
      },
    ],
    [t, language, shortDateFormat, timeFormat, account, priceFormatter, loadingId, onRowClick],
  );

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={2} sx={{ flex: 1, minHeight: 0 }}>
      <Grid container justifyContent="space-between" alignItems="center" gap={2} sx={{ px: mb.layout.cardPadding }}>
        <Grid item>
          <Typography variant="h5">{t('analytics.table.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('analytics.table.subtitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('analytics.table.dateRange', { range: dateRangeLabel })}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body2" color="text.secondary" display="inline">
            {t('dashboard.lastUpdate', { time: lastFetch.format(timeFormat) })}
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={onRefresh} disabled={isLoading} size="small">
            {t('dashboard.refresh')}
          </Button>
        </Grid>
      </Grid>

      <Grid container gap={1} alignItems="center" sx={{ px: mb.layout.cardPadding }}>
        <TextField
          value={search}
          variant="outlined"
          size="small"
          placeholder={t('search')}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <AnalyticsFilter />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={dateWindow}
          onChange={(_, v: DateWindow | null) => setDateWindow(v)}
          aria-label="date window">
          {DATE_WINDOWS.map((w) => (
            <ToggleButton key={w} value={w} sx={{ textTransform: 'none', px: 1.5 }}>
              {t(`analytics.table.window.${w}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {isNarrowed && !isLoading && (
          <Grid
            container
            item
            gap={2}
            alignItems="center"
            sx={{
              width: 'auto',
              px: 2,
              py: 1,
              backgroundColor: 'primary.light',
              borderRadius: '6px',
            }}>
            <Typography variant="body2" color="text.secondary">
              {t('analytics.totals.filteredCount', { count: totals.count, total: allRows.length })}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {priceFormatter(totals.income, false, 'h6')}
            </Typography>
          </Grid>
        )}
      </Grid>

      {!isLoading && summarySections.length > 0 && (
        <Grid container gap={1.5} alignItems="center" flexWrap="wrap" sx={{ px: mb.layout.cardPadding }}>
          {summarySections.map((s) => {
            const Icon = SECTION_META[s].icon;
            const count = filteredSectionCounts[s] ?? 0;
            return (
              <Grid key={s} container item gap={0.5} alignItems="center" sx={{ width: 'auto' }}>
                <Icon fontSize="small" color={count > 0 ? 'action' : 'disabled'} />
                <Typography variant="body2" color={count > 0 ? 'text.primary' : 'text.disabled'} fontWeight={600}>
                  {count}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`analytics.sections.${s}.title`)}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      )}

      {truncatedSections.length > 0 && !isLoading && (
        <Grid container sx={{ px: mb.layout.cardPadding }}>
          <Typography variant="caption" color="text.secondary">
            {t('analytics.showingNofM', {
              sections: truncatedSections.map((s) => t(`analytics.sections.${s}.title`)).join(', '),
            })}
          </Typography>
        </Grid>
      )}

      <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
        <TableComponent<UnifiedRow>
          fillHeight
          isLoading={isLoading}
          data={paginated}
          tableDetails={columns}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Grid>

      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={closeEditOrderDrawer}
        updateOrder={(_: OrderData) => {
          // Refresh underlying dashboard on order edit
          onRefresh();
        }}
      />
    </Grid>
  );
};
